export type DateRangePreset =
  | 'd7'
  | 'd30'
  | 'd90'
  | 'd365'
  | 'mtd'
  | 'lastMonth'
  | 'all'
  | 'custom';

export type ComparisonMode = 'none' | 'prevPeriod' | 'prevYear';

export const COMPARISON_ORDER: ComparisonMode[] = [
  'none',
  'prevPeriod',
  'prevYear',
];

export interface DateRangeValue {
  preset: DateRangePreset;
  /** Inclusive start, ISO string. Undefined means "no lower bound". */
  from?: string;
  /** Inclusive end, ISO string. Undefined means "up to now". */
  to?: string;
}

export const PRESET_ORDER: DateRangePreset[] = [
  'd7',
  'd30',
  'd90',
  'd365',
  'mtd',
  'lastMonth',
  'all',
  'custom',
];

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/** Resolve a preset key to a concrete date window. */
export function computePresetRange(preset: DateRangePreset): {
  from?: Date;
  to?: Date;
} {
  const now = new Date();
  switch (preset) {
    case 'd7':
    case 'd30':
    case 'd90':
    case 'd365': {
      const days =
        preset === 'd7'
          ? 7
          : preset === 'd30'
            ? 30
            : preset === 'd90'
              ? 90
              : 365;
      const from = startOfDay(now);
      from.setDate(from.getDate() - days);
      return { from, to: endOfDay(now) };
    }
    case 'mtd':
      return {
        from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: endOfDay(now),
      };
    case 'lastMonth':
      return {
        from: startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case 'all':
    case 'custom':
    default:
      return {};
  }
}

/** Build a {@link DateRangeValue} from a preset, resolving the ISO window. */
export function rangeValueFromPreset(preset: DateRangePreset): DateRangeValue {
  const { from, to } = computePresetRange(preset);
  return { preset, from: from?.toISOString(), to: to?.toISOString() };
}

export function valuesEqual(a: DateRangeValue, b: DateRangeValue): boolean {
  return (
    a.preset === b.preset &&
    (a.from ?? null) === (b.from ?? null) &&
    (a.to ?? null) === (b.to ?? null)
  );
}

/** Period-over-period comparison needs a bounded start (not "All time"). */
export function isComparisonSupported(value: DateRangeValue): boolean {
  return value.preset !== 'all' && !!value.from;
}
